import React, { useRef, useEffect } from 'react';
import Fade from '@mui/material/Fade';
import Modal from '@mui/material/Modal';

interface CustomModalProps {
  open: boolean;
  onClose: (isOpen: boolean) => void;
  name: string;
  glass?: boolean;
  blurs?: boolean;
  blurType?: 'success' | 'error';
  children: React.ReactNode;
  disableBackdropClick?: boolean;
  unsavedChanges?: boolean;
  handleUnsavedChanges?: () => void;
}

const CustomModal: React.FC<CustomModalProps> = ({
  open,
  onClose,
  children,
  name,
  glass,
  blurs,
  blurType = 'success',
  disableBackdropClick,
  unsavedChanges,
  handleUnsavedChanges,
  ...props
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      // Store the currently focused element
      previousActiveElement.current = document.activeElement as HTMLElement;

      // Focus the first focusable element inside the modal
      setTimeout(() => {
        if (modalRef.current) {
          const focusableElements = modalRef.current.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          const firstElement = focusableElements[0] as HTMLElement;
          if (firstElement) {
            firstElement.focus();
          }
        }
      }, 0);
    } else if (previousActiveElement.current) {
      // Restore focus when modal closes
      previousActiveElement.current.focus();
    }
  }, [open]);

  const handleBackdropClick = () => {
    if (unsavedChanges && handleUnsavedChanges) {
      handleUnsavedChanges();
    } else {
      if (!disableBackdropClick) {
        onClose(false);
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      if (!modalRef.current) return;

      const focusableElements = Array.from(
        modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ) as HTMLElement[];

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // If shift + tab
      if (event.shiftKey) {
        if (focusableElements.includes(document.activeElement as HTMLElement)) {
          const currentIndex = focusableElements.indexOf(
            document.activeElement as HTMLElement
          );
          if (currentIndex === 0) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          event.preventDefault();
          lastElement.focus();
        }
      }
      // If tab
      else {
        if (focusableElements.includes(document.activeElement as HTMLElement)) {
          const currentIndex = focusableElements.indexOf(
            document.activeElement as HTMLElement
          );
          if (currentIndex === focusableElements.length - 1) {
            event.preventDefault();
            firstElement.focus();
          }
        } else {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => onClose(false)}
      BackdropProps={{
        timeout: 250,
        className: disableBackdropClick ? 'backdrop--disable' : '',
        onClick: handleBackdropClick,
      }}
      {...props}
      disableAutoFocus
      disableEnforceFocus={false}
      disableRestoreFocus={false}
    >
      <Fade in={open} timeout={250}>
        <div
          ref={modalRef}
          className={`modal ${glass ? 'modal--glass' : ''} modal--${name} ${
            blurs ? 'modal--blurs' : ''
          }`}
          tabIndex={-1}
          onKeyDown={handleKeyDown}
          role="dialog"
          aria-modal="true"
        >
          {children}
          {blurs && (
            <>
              {blurType === 'success' && <div className="blur-1"></div>}
              {blurType === 'success' && <div className="blur-2"></div>}
              {blurType === 'error' && <div className="blur-error-1"></div>}
              {blurType === 'error' && <div className="blur-error-2"></div>}
            </>
          )}
        </div>
      </Fade>
    </Modal>
  );
};

export default CustomModal;
